import {
  Box,
  CrudMode,
  DataDisplay,
  formatDate,
  formatSchoolYear,
  Grid,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Input, Select } from "components";
import { LkIpeds, NscCollege, NscCollegeEditResource, NscCollegeViewResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";

export const NscCollegePageBody = (props: {
  crudMode: CrudMode;
  nscCollege: NscCollege;
  setNscCollege: (value: NscCollege) => void;
  viewResource: NscCollegeViewResource;
  editResource: NscCollegeEditResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const properties = props.viewResource.properties || [];
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();

  const firstNameField = () => {
    const propertyName = pn<NscCollege>("firstName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.nscCollege.firstName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.nscCollege.firstName}
        onChange={(e) => {
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.firstName = e.target.value;
            })
          );
        }}
      />
    );
  };

  const lastNameField = () => {
    const propertyName = pn<NscCollege>("lastName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.nscCollege.lastName}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.nscCollege.lastName}
        onChange={(e) => {
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.lastName = e.target.value;
            })
          );
        }}
      />
    );
  };

  const yearField = () => {
    const propertyName = pn<NscCollege>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.nscCollege.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.nscCollege.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  const termField = () => {
    const propertyName = pn<NscCollege>("termId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.nscCollege.term?.name} {props.nscCollege.term?.name == "Fall" ? props.nscCollege.year : props.nscCollege.year! + 1}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.gradeLevelTerms?.find((x) => x.id === props.nscCollege.termId)}
        options={props.editResource.gradeLevelTerms}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.termId = value?.id;
            })
          )
        }
      />
    );
  };

  const collegeNameField = () => {
    const propertyName = pn<NscCollege>("collegeName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.nscCollege.collegeName}</DataDisplay>
    ) : (
      <Select
        value={{ instnm: props.nscCollege.collegeName ?? "" } as LkIpeds}
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        fetchFunction={(searchString: string) => ApiService.search.ipeds(searchString)}
        getOptionLabel={(option) => option.instnm ?? ""}
        onChange={(e, value) =>
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.collegeName = value?.instnm;
              draft.collegeState = value?.stabbr;
              draft.collegeCode = value?.opeid?.toString();
              draft.collegeTypeId = value?.iclevel === -3 ? undefined : value?.iclevel;

              if (value?.control === 1) {
                draft.publicOrPrivate = "Public";
              } else if (value?.control === 2 || value?.control === 3) {
                draft.publicOrPrivate = "Private";
              } else draft.publicOrPrivate = undefined;
            })
          )
        }
      />
    );
  };

  const graduationDateField = () => {
    const propertyName = pn<NscCollege>("graduationDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.nscCollege.graduationDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.nscCollege.graduationDate}
        onChange={(value) => {
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.graduationDate = value;
            })
          );
        }}
      />
    );
  };

  const enrollmentBeginField = () => {
    const propertyName = pn<NscCollege>("enrollmentBegin");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.nscCollege.enrollmentBegin)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.nscCollege.enrollmentBegin}
        onChange={(value) => {
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.enrollmentBegin = value;
            })
          );
        }}
      />
    );
  };

  const enrollmentEndField = () => {
    const propertyName = pn<NscCollege>("enrollmentEnd");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.nscCollege.enrollmentEnd)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.nscCollege.enrollmentEnd}
        onChange={(value) => {
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.enrollmentEnd = value;
            })
          );
        }}
      />
    );
  };

  const degreeTitleField = () => {
    const propertyName = pn<NscCollege>("degreeTitle");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.nscCollege.degreeTitle}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.nscCollege.degreeTitle}
        onChange={(e) => {
          props.setNscCollege(
            produce(props.nscCollege, (draft) => {
              draft.degreeTitle = e.target.value;
            })
          );
        }}
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      {/* <Grid item xs={smallFieldBreakpoints}>
        {firstNameField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {lastNameField()}
      </Grid> */}

      <Grid item xs={smallFieldBreakpoints}>
        {yearField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {termField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {collegeNameField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {enrollmentBeginField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {enrollmentEndField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {graduationDateField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {degreeTitleField()}
      </Grid>
    </Grid>
  );
};
